import { authApi, siteApi } from "@api";
import { IconIDCard, IconMaterialIdCard } from "@assets/icons";
import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import { AppDispatch } from "@stores";
import authSlice from "@stores/auth/slices";
import { Button, TextField, UpdatePersonalInfoRequest, useHandleApiResponse } from "kz-ui-sdk";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { z } from "zod";

const schema = z.object({
  fullName: ValidatorSchema.legalName,
});

type FormValues = z.infer<typeof schema>;

interface FormLegalNameProps {
  onUpdateSuccess?: () => void;
  mode: "update" | "add";
}

const FormLegalName = ({ onUpdateSuccess, mode }: FormLegalNameProps) => {
  const { t } = useTranslation();
  const { handleApiResponse } = useHandleApiResponse({
    toast,
  });

  const [updatePersonalInfo, { isLoading: isSubmitting }] = siteApi.useUpdatePersonalInfoMutation();
  const dispatch = useDispatch<AppDispatch>();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    // fullname will be passed as firstname
    const request: UpdatePersonalInfoRequest = {
      firstname: data.fullName,
    };

    const result = await updatePersonalInfo(request);
    handleApiResponse(result, {
      successMessage: t("updated legal name successfully"),
      onSuccess: async () => {
        // refresh self
        const result = await dispatch(
          authApi.endpoints.getProfile.initiate(undefined, {
            forceRefetch: true,
          }),
        );
        if (result.data) {
          dispatch(authSlice.actions.updateProfile(result.data));
        }
        onUpdateSuccess?.();
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
      autoComplete="off"
    >
      <div className="flex flex-col gap-4">
        <div className="mx-7 mb-[7px] flex items-center justify-between gap-5">
          <IconIDCard className="flex-shrink-0" />
          <div className="w-[65%] text-xs text-content-soft">
            {t("please enter exactly as it appears on your ID to ensure smooth and fast verification")}
          </div>
        </div>
        <TextField
          placeholder={t("your full legal name")}
          {...register("fullName")}
          error={!!errors.fullName}
          helperText={t(errors.fullName?.message || "")}
          size="sm"
          icon={<IconMaterialIdCard />}
          classes={{
            "input&": "w-full !px-0 !pl-11 text-start",
            "iconContainer&": "!left-3 !top-[10px]",
          }}
        />
      </div>
      <Button
        type="submit"
        variant="primary"
        className="w-full"
        loading={isSubmitting}
        disabled={isSubmitting}
        size="lg"
      >
        {mode === "update" ? t("Update") : t("finish & login")}
      </Button>
    </form>
  );
};

export default FormLegalName;
